import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Edit,
  Save,
  Cancel,
  Person,
  SportsTennis,
  TrendingUp,
  EmojiEvents,
  Security,
  Language,
  Delete,
  Upload,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';

interface UserProfile {
  id: number;
  name: string;
  email: string;
  avatar_url?: string;
  skill_level: number;
  preferred_side: string;
  weekly_availability?: string;
  club_id?: number;
  club_name?: string;
  is_active: boolean;
  is_admin: boolean;
  total_matches: number;
  total_wins: number;
  win_rate: number;
  created_at: string;
  updated_at: string;
}

interface Club {
  id: number;
  name: string;
  location: string;
  is_active: boolean;
}

export const SettingsPage: React.FC = () => {
  const { updateUser } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [deleteDialog, setDeleteDialog] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    skill_level: 3,
    preferred_side: 'right',
    weekly_availability: '',
    club_id: '',
  });

  useEffect(() => {
    fetchProfile();
    fetchClubs();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await apiService.getCurrentUser();
      const data = response.data as any;
      setProfile(data as UserProfile);
      setFormData({
        name: data.name,
        email: data.email,
        skill_level: data.skill_level,
        preferred_side: data.preferred_side,
        weekly_availability: data.weekly_availability || '',
        club_id: data.club_id?.toString() || '',
      });
    } catch (error: any) {
      console.error('Error fetching profile:', error);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.detail || 'Failed to fetch profile', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchClubs = async () => {
    try {
      const response = await apiService.getClubs();
      setClubs((response.data || []) as any);
    } catch (error) {
      console.error('Error fetching clubs:', error);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      const response = await apiService.updateCurrentUser({
        ...formData,
        club_id: formData.club_id ? parseInt(formData.club_id) : null,
      } as any);
      const updatedProfile = response.data as any;
      setProfile(updatedProfile as UserProfile);
      setEditing(false);
      setSnackbar({ open: true, message: 'Profile updated successfully', severity: 'success' });
      updateUser(updatedProfile);
    } catch (error: any) {
      console.error('Error updating profile:', error);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.detail || 'Failed to update profile', 
        severity: 'error' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setFormData({
      name: profile?.name || '',
      email: profile?.email || '',
      skill_level: profile?.skill_level || 3,
      preferred_side: profile?.preferred_side || 'right',
      weekly_availability: profile?.weekly_availability || '',
      club_id: profile?.club_id?.toString() || '',
    });
    setEditing(false);
  };

  const handleDeleteAccount = async () => {
    try {
      await apiService.deleteCurrentUser();
      setSnackbar({ open: true, message: 'Account deleted successfully', severity: 'success' });
      // Redirect to login or home page
      window.location.href = '/login';
    } catch (error: any) {
      console.error('Error deleting account:', error);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.detail || 'Failed to delete account', 
        severity: 'error' 
      });
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box mb={4}>
      <Typography variant="h4" component="h1" gutterBottom>
          Settings & Profile
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your account settings and preferences
        </Typography>
      </Box>

      <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3}>
        {/* Profile Card */}
        <Box flex={{ xs: 'none', md: '0 0 33.333%' }}>
          <Card>
            <CardContent>
              <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
                <Avatar
                  src={profile?.avatar_url}
                  sx={{ width: 120, height: 120, mb: 2 }}
                >
                  <Person sx={{ fontSize: 60 }} />
                </Avatar>
                <Typography variant="h5" gutterBottom>
                  {profile?.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {profile?.email}
      </Typography>
                <Box display="flex" gap={1} mb={2}>
                  <Chip
                    label={`Level ${profile?.skill_level}`}
                    color="primary"
                    size="small"
                  />
                  <Chip
                    label={profile?.preferred_side}
                    variant="outlined"
                    size="small"
                  />
                  {profile?.is_admin && (
                    <Chip
                      label="Admin"
                      color="warning"
                      size="small"
                    />
                  )}
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<Upload />}
                  size="small"
                >
                  Change Avatar
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card sx={{ mt: 2 }}>
            <CardHeader title="Quick Stats" />
            <CardContent>
        <List>
                <ListItem>
                  <ListItemIcon>
                    <SportsTennis color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Total Matches"
                    secondary={profile?.total_matches || 0}
                  />
                </ListItem>
                <ListItem>
              <ListItemIcon>
                    <TrendingUp color="success" />
              </ListItemIcon>
              <ListItemText 
                    primary="Win Rate"
                    secondary={`${profile?.win_rate || 0}%`}
              />
          </ListItem>
                <ListItem>
              <ListItemIcon>
                    <EmojiEvents color="warning" />
              </ListItemIcon>
              <ListItemText 
                    primary="Total Wins"
                    secondary={profile?.total_wins || 0}
              />
          </ListItem>
        </List>
            </CardContent>
          </Card>
        </Box>

        {/* Settings Forms */}
        <Box flex={1}>
          {/* Personal Information */}
          <Card sx={{ mb: 3 }}>
            <CardHeader
              title="Personal Information"
              action={
                editing ? (
                  <Box display="flex" gap={1}>
                    <Button
                      variant="outlined"
                      startIcon={<Cancel />}
                      onClick={handleCancelEdit}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Save />}
                      onClick={handleSaveProfile}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </Button>
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() => setEditing(true)}
                  >
                    Edit
                  </Button>
                )
              }
            />
            <CardContent>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    disabled={!editing}
                  />
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    disabled={!editing}
                  />
                </Box>
                <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
                  <FormControl fullWidth disabled={!editing}>
                    <InputLabel>Skill Level</InputLabel>
                    <Select
                      value={formData.skill_level}
                      label="Skill Level"
                      onChange={(e) => handleInputChange('skill_level', e.target.value)}
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((level) => (
                        <MenuItem key={level} value={level}>
                          Level {level}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth disabled={!editing}>
                    <InputLabel>Preferred Side</InputLabel>
                    <Select
                      value={formData.preferred_side}
                      label="Preferred Side"
                      onChange={(e) => handleInputChange('preferred_side', e.target.value)}
                    >
                      <MenuItem value="left">Left</MenuItem>
                      <MenuItem value="right">Right</MenuItem>
                      <MenuItem value="both">Both</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <FormControl fullWidth disabled={!editing}>
                  <InputLabel>Club</InputLabel>
                  <Select
                    value={formData.club_id}
                    label="Club"
                    onChange={(e) => handleInputChange('club_id', e.target.value)}
                  >
                    <MenuItem value="">No Club</MenuItem>
                    {clubs.map((club) => (
                      <MenuItem key={club.id} value={club.id.toString()}>
                        {club.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Weekly Availability"
                  multiline
                  rows={3}
                  value={formData.weekly_availability}
                  onChange={(e) => handleInputChange('weekly_availability', e.target.value)}
                  disabled={!editing}
                  placeholder="Describe your weekly availability for matches..."
                />
              </Box>
            </CardContent>
          </Card>

          {/* Preferences */}
          <Card sx={{ mb: 3 }}>
            <CardHeader title="Preferences" />
            <CardContent>
              <Box display="flex" flexDirection="column" gap={2}>
                <FormControlLabel
                  control={<Switch defaultChecked />}
                  label="Email notifications for new matches"
                />
                <FormControlLabel
                  control={<Switch defaultChecked />}
                  label="Email notifications for tournament updates"
                />
                <FormControlLabel
                  control={<Switch />}
                  label="Push notifications"
                />
                <FormControlLabel
                  control={<Switch defaultChecked />}
                  label="Weekly match summary emails"
                />
              </Box>
            </CardContent>
          </Card>

          {/* Security */}
          <Card sx={{ mb: 3 }}>
            <CardHeader title="Security" />
            <CardContent>
              <Box display="flex" flexDirection="column" gap={2}>
                <Button variant="outlined" startIcon={<Security />}>
                  Change Password
                </Button>
                <Button variant="outlined" startIcon={<Language />}>
                  Two-Factor Authentication
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Danger Zone */}
          <Card sx={{ border: '1px solid', borderColor: 'error.main' }}>
            <CardHeader 
              title="Danger Zone" 
              titleTypographyProps={{ color: 'error.main' }}
            />
            <CardContent>
              <Alert severity="warning" sx={{ mb: 2 }}>
                These actions are irreversible. Please proceed with caution.
              </Alert>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={() => setDeleteDialog(true)}
              >
                Delete Account
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Delete Account Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <Typography paragraph>
            Are you sure you want to delete your account? This action cannot be undone.
          </Typography>
          <Typography color="error" paragraph>
            All your data, matches, and statistics will be permanently deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteAccount} color="error" variant="contained">
            Delete Account
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}; 
